import { Provider } from '@angular/core';
import { PERSISTED_SLICES, PersistedSlice } from '../../oidc/store/persisted-state';
import { tidy } from '../../oidc/store/tidy';
import { settingsFeature } from './settings.feature';
import { SettingsState, initialSettingsState } from './settings.models';

/**
 * Fields whose object keys are values rather than a fixed set of properties, so `tidy`
 * must pass them through unchanged. Measured against the defaults it would find no key it
 * recognises and hand back an empty object, silently dropping the user's data.
 *
 * `homescreen` is keyed by tile id, `personalContactDetails` has an empty default and so
 * offers nothing to match against either.
 *
 * The `satisfies` makes a renamed field a compile error instead of silent data loss.
 */
const DATA_KEYED_FIELDS: ReadonlySet<string> = new Set(
  ['homescreen', 'personalContactDetails'] satisfies (keyof SettingsState)[],
);

/**
 * Registers the settings slice for hydration and persistence. Sits next to the feature so
 * the knowledge about its shape stays here rather than in the application bootstrap.
 */
export const provideSettingsPersistence = (): Provider => ({
  provide: PERSISTED_SLICES,
  multi: true,
  useValue: {
    name: settingsFeature.name,
    reshape: stored => tidy(stored, initialSettingsState, DATA_KEYED_FIELDS),
  } satisfies PersistedSlice<SettingsState>,
});
